/**
 * Site Induction wizard — per-step Zod schemas.
 *
 * Lifted verbatim from
 * `apps/mobile/screens/wizards/WizardSiteInductionScreen.tsx`
 * (Phase 15a).
 *
 * Step order:
 *
 *   1. context   — project title, address, scope description
 *   2. emergency — reporting procedures (induction-specific)
 *   3. ppe       — PPE checklist
 *   4. hazards   — site hazards (label-only, no severity)
 *   5. signoff   — free-text sign-off list
 *   6. preview   — read-only summary (no validation)
 *
 * Site rules + welfare arrangements + the project-level emergency facts
 * (assembly point, fire strategy, nearest hospital, first aid arrangements) are
 * single project-level facts edited once in project settings (the former
 * `rules` + `welfare` steps were removed and the `emergency` step was trimmed to
 * just the induction-specific `reportingProcedures`) — they render into the
 * induction from the project row.
 *
 * Unlike RAMS/CPP, site hazards are label-only — induction wizards
 * record what's on site, not the severity-rated assessment that goes
 * into a RAMS.
 */
import { z } from "zod";

// ─── Shared sub-schemas ────────────────────────────────────────────────────

export const SiteInductionHazardEntrySchema = z.object({
  id: z.string(),
  label: z.string().min(1),
});
export type SiteInductionHazardEntry = z.infer<
  typeof SiteInductionHazardEntrySchema
>;

// ─── Per-step schemas ──────────────────────────────────────────────────────

export const SiteInductionContextStepSchema = z.object({
  projectTitle: z.string().min(2, "Project title is required"),
  siteAddress: z.string().min(3, "Site address is required"),
  scopeDescription: z.string().optional().default(""),
});
export type SiteInductionContextStepData = z.infer<
  typeof SiteInductionContextStepSchema
>;

// Site rules are a single project-level fact now — edited once in project
// settings and rendered into the induction from the project row, so the
// induction wizard no longer has a `rules` step.

// Assembly point + fire strategy + nearest hospital + first aid arrangements
// are single project-level facts now — edited once in project settings and
// rendered into the induction from the project row. Only `reportingProcedures`
// (how accidents / near misses / unsafe conditions are reported) stays in the
// wizard: it's induction-specific and not a project field.
export const SiteInductionEmergencyStepSchema = z.object({
  reportingProcedures: z
    .string()
    .min(5, "Reporting procedures are required"),
});
export type SiteInductionEmergencyStepData = z.infer<
  typeof SiteInductionEmergencyStepSchema
>;

export const SiteInductionPpeStepSchema = z.object({
  ppe: z.array(z.string()).min(1, "Pick at least one PPE item"),
});
export type SiteInductionPpeStepData = z.infer<
  typeof SiteInductionPpeStepSchema
>;

export const SiteInductionHazardsStepSchema = z.object({
  hazards: z
    .array(SiteInductionHazardEntrySchema)
    .min(1, "Add at least one site hazard"),
});
export type SiteInductionHazardsStepData = z.infer<
  typeof SiteInductionHazardsStepSchema
>;

// Welfare arrangements are a single project-level fact now — edited once in
// project settings and rendered into the induction from the project row, so the
// induction wizard no longer has a `welfare` step.

export const SiteInductionSignOffStepSchema = z.object({
  signOffList: z
    .string()
    .min(2, "List who needs to complete the induction"),
});
export type SiteInductionSignOffStepData = z.infer<
  typeof SiteInductionSignOffStepSchema
>;

/** Preview is read-only — no validation. */
export const SiteInductionPreviewStepSchema = z.object({});
export type SiteInductionPreviewStepData = z.infer<
  typeof SiteInductionPreviewStepSchema
>;

// ─── Step ordering + composite ─────────────────────────────────────────────

export const SITE_INDUCTION_WIZARD_STEPS = [
  { id: "context", schema: SiteInductionContextStepSchema },
  { id: "emergency", schema: SiteInductionEmergencyStepSchema },
  { id: "ppe", schema: SiteInductionPpeStepSchema },
  { id: "hazards", schema: SiteInductionHazardsStepSchema },
  { id: "signoff", schema: SiteInductionSignOffStepSchema },
  { id: "preview", schema: SiteInductionPreviewStepSchema },
] as const;

export const SiteInductionWizardFullSchema = z.object({
  ...SiteInductionContextStepSchema.shape,
  ...SiteInductionEmergencyStepSchema.shape,
  ...SiteInductionPpeStepSchema.shape,
  ...SiteInductionHazardsStepSchema.shape,
  ...SiteInductionSignOffStepSchema.shape,
});
export type SiteInductionWizardData = z.infer<
  typeof SiteInductionWizardFullSchema
>;
